/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { memo, useState, useContext } from 'react';
import DetailSummaryComponent from '../../../shared/DetailSummaryComponent/DetailSummaryComponent';
import '../../../../scss/ViewDetailsTheme.scss';
import {
  getConvertedValue,
  getConvertedNullValue,
  getMaxConvertedValueFromArray
} from '../../../shared/Utils/unitHandling/unitHandling';
import { ConversionType } from '../../../shared/Utils/unitHandling/UnitModel';
import { convertStackedDataForEnergyDisaggregationChart } from '../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import descriptors from '../../../../locales/en-US/descriptors';
import { useTranslation } from '@scuf/localization';
import {
  IAny,
  IEmaasEnergyUsageComponentDataV2
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import StackedColumnChart from '../../../shared/StackedColumnChart/StackedColumnChart';
import { EnergyUsageField } from '../../../shared/Enums';
import { ApiContext } from '../../../../context/ApiContext';
import { UnitUtility } from '@bmsp/bmsp.web.component.common';
import { PERCENT_CHANGE_THRESHOLD } from '../../../shared/Utils/Constants';

export interface IEnergyUsagesAllUtilityDetailsComponentProps {
  data: IEmaasEnergyUsageComponentDataV2;
  requestContext?: IAny;
  intervalType?: string;
}
const EmaasEUsageAllUtilDetailsBasicComponent = (
  props: IEnergyUsagesAllUtilityDetailsComponentProps
) => {
  const [t, lng] = useTranslation();
  const { data, requestContext } = props;
  const apiContext = useContext(ApiContext);
  const { userPreferedUnit } = apiContext;
  let fromUnit;
  // if (true) {
  //   fromUnit = userPreferedUnit as any;
  // } else {
  //   fromUnit = energyMeasurementUnit;
  // }

  fromUnit = userPreferedUnit as any;

  const { nilmAggregation, energyMeasurementUnit, summary } = data;
  const {
    totalBaselineConsumption,
    totalConsumption,
    totalEnergyDeviation,
    totalEnergyDeviationPercentage
  } = nilmAggregation.summary as IAny;
  const valuesArray = [totalConsumption, totalBaselineConsumption];

  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    ConversionType.ELECTRICITY,
    fromUnit, //energyMeasurementUnit
    valuesArray,
    energyMeasurementUnit
  );

  const aggregationSummaryData = {
    actualCost: getConvertedValue(
      ConversionType.ELECTRICITY,
      energyMeasurementUnit,
      totalConsumption,
      maxValueUnitConversion.Unit
    ).Value,
    baselineCost: getConvertedValue(
      ConversionType.ELECTRICITY,
      energyMeasurementUnit,
      totalBaselineConsumption,
      maxValueUnitConversion.Unit
    ).Value,
    costSavings:
      apiContext.showChangeWhenData &&
      (isNaN(Number(totalConsumption)) ||
        isNaN(Number(totalBaselineConsumption)) ||
        Number(totalConsumption) === 0 ||
        Number(totalBaselineConsumption) === 0 ||
        Math.abs(Number(totalEnergyDeviationPercentage)) >
          PERCENT_CHANGE_THRESHOLD)
        ? null
        : getConvertedValue(
            ConversionType.ELECTRICITY,
            energyMeasurementUnit,
            totalEnergyDeviation,
            maxValueUnitConversion.Unit
          ).Value,
    costSavingsPercentage: getConvertedNullValue(totalEnergyDeviationPercentage)
  };

  const [nilmGraphData] = useState(
    convertStackedDataForEnergyDisaggregationChart(
      nilmAggregation.energyDisaggregation,
      fromUnit,
      energyMeasurementUnit,
      requestContext,
      lng
    )
  );

  let deviationScaledData;
  if (aggregationSummaryData.costSavings === 0 && totalEnergyDeviation !== 0) {
    deviationScaledData = getConvertedValue(
      ConversionType.ELECTRICITY,
      energyMeasurementUnit,
      totalEnergyDeviation,
      maxValueUnitConversion.Unit
    );
  }
  return (
    <div className="allUtility-performance-detail-container energyDisaggregation">
      <div>
        <DetailSummaryComponent
          aggregationData={aggregationSummaryData}
          measurementUnit={maxValueUnitConversion.Unit}
          detailType="detailSummaryWithoutCurrencyNILM"
          deviationScaledData={deviationScaledData}
          category={EnergyUsageField.ENERGY_USAGE}
        />
        <StackedColumnChart
          unit={nilmGraphData.unit}
          chartData={nilmGraphData as IAny}
          isAllDataAvailable={false}
          stackType={'nilmDisaggreagtion'}
          intervalType={props.intervalType}
        />
      </div>
    </div>
  );
};

export default memo(EmaasEUsageAllUtilDetailsBasicComponent);
